import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ChangeOrderDetail = () => {
  const { id } = useParams();
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [changeOrder, setChangeOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminNotes, setShowAdminNotes] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchChangeOrder();
  }, [id]);

  const fetchChangeOrder = async () => {
    try {
      const response = await fetch(`${API_URL}/change-orders/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch change order');
      const data = await response.json();
      setChangeOrder(data);
      setAdminNotes(data.admin_notes || '');
    } catch (error) {
      console.error('Error fetching change order:', error);
      toast.error('Failed to load change order');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this change order?`)) return;

    try {
      const payload = { status: newStatus };
      if (showAdminNotes) {
        payload.admin_notes = adminNotes;
      }

      const response = await fetch(`${API_URL}/change-orders/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success(`Change order ${newStatus}`);
      fetchChangeOrder();
      setShowAdminNotes(false);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this change order? This cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/change-orders/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete change order');
      toast.success('Change order deleted');
      navigate('/change-orders');
    } catch (error) {
      console.error('Error deleting change order:', error);
      toast.error('Failed to delete change order');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#92400e' },
      approved: { bg: '#d1fae5', color: '#065f46' },
      rejected: { bg: '#fee2e2', color: '#991b1b' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '6px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        background: style.bg,
        color: style.color,
        textTransform: 'uppercase'
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!changeOrder) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Change order not found</div>;
  }

  const priceDiff = parseFloat(changeOrder.price_difference || 0);
  const isIncrease = priceDiff >= 0;

  return (
    <div style={{ padding: '24px', background: '#f9fafb', minHeight: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/change-orders')}
            style={{
              padding: '8px',
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex'
            }}
          >
            <ArrowLeftIcon style={{ width: 20, height: 20 }} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ fontSize: '32px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                {changeOrder.title}
              </h1>
              {getStatusBadge(changeOrder.status)}
            </div>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
              Requested {new Date(changeOrder.requested_at).toLocaleDateString()} by {changeOrder.requested_by_name}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {changeOrder.status === 'pending' && (
            <>
              <button
                onClick={() => navigate(`/change-orders/edit/${id}`)}
                style={{
                  padding: '10px 16px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <PencilIcon style={{ width: 16, height: 16 }} /> Edit
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowAdminNotes(true)}
                    style={{
                      padding: '10px 16px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <CheckCircleIcon style={{ width: 16, height: 16 }} /> Approve
                  </button>
                  <button
                    onClick={() => handleStatusChange('rejected')}
                    style={{
                      padding: '10px 16px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <XCircleIcon style={{ width: 16, height: 16 }} /> Reject
                  </button>
                </>
              )}
            </>
          )}
          {isAdmin && (
            <button
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <TrashIcon style={{ width: 16, height: 16 }} /> Delete
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gap: '24px' }}>
        {/* Service Call Info */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Service Call</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Title</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{changeOrder.service_call_title || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Client</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{changeOrder.client_name || '—'}</div>
            </div>
            {changeOrder.scheduled_date && (
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Scheduled</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>{new Date(changeOrder.scheduled_date).toLocaleDateString()}</div>
              </div>
            )}
          </div>
        </div>

        {/* Change Details */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Change Details</h2>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Description</div>
            <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{changeOrder.description}</div>
          </div>

          {changeOrder.reason && (
            <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Reason</div>
              <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{changeOrder.reason}</div>
            </div>
          )}
        </div>

        {/* Scope Comparison */}
        {(changeOrder.original_scope || changeOrder.new_scope) && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Scope Comparison</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Original Scope</div>
                <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '6px', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {changeOrder.original_scope || 'Not specified'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>New Scope</div>
                <div style={{ padding: '12px', background: '#d1fae5', borderRadius: '6px', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {changeOrder.new_scope || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Price Adjustment */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Price Adjustment</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Original Price</div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {changeOrder.original_price ? `$${parseFloat(changeOrder.original_price).toLocaleString()}` : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>New Price</div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {changeOrder.new_price ? `$${parseFloat(changeOrder.new_price).toLocaleString()}` : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Difference</div>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: isIncrease ? '#dc2626' : '#059669',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {isIncrease ? (
                  <ArrowTrendingUpIcon style={{ width: 24, height: 24 }} />
                ) : (
                  <ArrowTrendingDownIcon style={{ width: 24, height: 24 }} />
                )}
                {priceDiff >= 0 ? '+' : ''}${Math.abs(priceDiff).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Photos */}
        {changeOrder.photos && changeOrder.photos.length > 0 && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Documentation Photos ({changeOrder.photos.length})</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
              {changeOrder.photos.map(photo => (
                <div key={photo.id} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                  <img
                    src={photo.photo_url}
                    alt="Change Order"
                    style={{ width: '100%', height: '200px', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => window.open(photo.photo_url, '_blank')}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Notes */}
        {(changeOrder.admin_notes || changeOrder.reviewed_by) && (
          <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '12px', padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#92400e' }}>Admin Review</h2>
            {changeOrder.reviewed_by && (
              <div style={{ marginBottom: '12px', fontSize: '14px', color: '#92400e' }}>
                Reviewed by {changeOrder.reviewed_by_name} on {new Date(changeOrder.reviewed_at).toLocaleDateString()}
              </div>
            )}
            {changeOrder.admin_notes && (
              <div style={{ fontSize: '14px', color: '#78350f', whiteSpace: 'pre-wrap' }}>
                {changeOrder.admin_notes}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Admin Notes Modal */}
      {showAdminNotes && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Approve Change Order</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Admin Notes (Optional)</label>
              <textarea
                rows="4"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add any notes about this approval..."
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAdminNotes(false)}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', fontSize: '14px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusChange('approved')}
                style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
              >
                Approve Change Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeOrderDetail;